/**
 * @file React hook driving the entire export state machine on the Web side.
 *
 *   loading
 *     → (Firestore snapshot pending)
 *   awaiting_approval
 *     → write web_client_info + emit fingerprint to UI
 *   approved
 *     → decrypt encrypted_kek, importKey(extractable=false),
 *       transition session to consumed
 *   consumed
 *     → run streaming export to a user-chosen file, transition session to
 *       completed (or failed)
 *   completed | failed | rejected | expired
 *     → terminal
 *
 * Heavy lifting (Firestore fetches, decryption, zip writing) lives in
 * `lib/exportRunner.ts` and `lib/zipStream.ts`. This hook only sequences the
 * top-level state machine and surfaces a flat state for the UI.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type Firestore, type Unsubscribe } from "firebase/firestore";
import type { FirebaseStorage } from "firebase/storage";

import { decryptEncryptedKek, importKekNonExtractable, zeroFill } from "../lib/crypto";
import { type ExportProgress, runExport } from "../lib/exportRunner";
import {
  type SessionEvent,
  markSessionCompleted,
  markSessionConsumed,
  markSessionFailed,
  recordWebClientArrival,
  subscribeSession,
} from "../lib/sessionRepo";
import { decodeBase64Url } from "../lib/url";
import {
  BrowserUnsupportedError,
  type ZipEntry,
  openSaveFile,
  streamZipToFile,
} from "../lib/zipStream";
import type { ExportFailureCategory, ExportPhase, ExportSessionDoc } from "../types";

export interface UseExportSessionParams {
  firestore: Firestore;
  storage: FirebaseStorage;
  sessionId: string;
  transportKeyB64Url: string;
  /** UA string for awaiting_approval handshake (defaults to navigator). */
  userAgent?: string;
}

export interface UseExportSessionState {
  phase: ExportPhase;
  session: (ExportSessionDoc & { id: string }) | null;
  progress: ExportProgress | null;
  error: { category: ExportFailureCategory; detail?: string } | null;
  startExport: () => Promise<void>;
}

const INITIAL_PROGRESS: ExportProgress = {
  phase: "metadata",
  entriesDone: 0,
  entriesTotal: 0,
  observationsDone: 0,
  digestsDone: 0,
  mediaDone: 0,
  mediaTotal: 0,
  mediaBytes: 0,
};

/**
 * @what: React hook driving the entire Web-side export state machine.
 *   Returns a flat state object (`phase`, `session`, `progress`, `error`) plus
 *   a `startExport()` action that the UI invokes once the user has picked a
 *   save location.
 * @why: 4 種類の画面 (待機 / 進捗 / 完了 / エラー) すべてが同じ Firestore listener
 *   と KEK ライフサイクルを共有するため、画面ごとではなく feature 単位で 1 つの
 *   hook に集約する。`useEffect` の cleanup で listener を確実に解放することで
 *   pending セッションのリーク (T-12 抑止) を防ぐ。KEK は `useRef<CryptoKey>` で
 *   保持し、生バイトはメモリ上に残らない (対策 D)。
 * @failure: state の `phase` が `failed` / `rejected` / `expired` のいずれかに
 *   遷移し、`error.category` が以下いずれかに正規化される:
 *   - `firestore_permission_error` (listener / update が permission denied)
 *   - `decryption_error` (transport_key 不一致 / KEK アンラップ失敗)
 *   - `disk_write_error` (zip stream / showSaveFilePicker 失敗)
 *   - `browser_unsupported` (FSA API 非対応ブラウザ)
 *   - `session_invalid` (Firestore に doc が無い / TTL 切れ)
 *   いずれも UI に詳細メッセージを出さず汎用カテゴリのみ表示する
 *   (`feedback_no_internal_errors_in_ui.md` 準拠)。
 */
export function useExportSession(params: UseExportSessionParams): UseExportSessionState {
  const { firestore, storage, sessionId, transportKeyB64Url } = params;
  const userAgent =
    params.userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "unknown");

  const [phase, setPhase] = useState<ExportPhase>("loading");
  const [session, setSession] = useState<(ExportSessionDoc & { id: string }) | null>(null);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [error, setError] = useState<UseExportSessionState["error"]>(null);

  // KEK is held only as a CryptoKey (never as raw bytes). One per session.
  const kekRef = useRef<CryptoKey | null>(null);
  const arrivalRecordedRef = useRef(false);

  const setFailed = useCallback(
    (category: ExportFailureCategory, detail?: string) => {
      setPhase("failed");
      setError(detail !== undefined ? { category, detail } : { category });
      void markSessionFailed(firestore, sessionId, category).catch(() => {
        // best-effort; ignore secondary errors
      });
    },
    [firestore, sessionId],
  );

  // ── Subscribe to the session ────────────────────────────────────────────
  useEffect(() => {
    let unsubscribe: Unsubscribe | undefined;
    try {
      unsubscribe = subscribeSession(firestore, sessionId, (event: SessionEvent) => {
        if (event.kind === "missing") {
          setPhase("expired");
          setError({ category: "session_invalid" });
          return;
        }
        if (event.kind === "error") {
          setPhase("failed");
          setError({ category: "firestore_permission_error" });
          return;
        }
        setSession(event.session);
      });
    } catch {
      setPhase("failed");
      setError({ category: "firestore_permission_error" });
    }
    return () => {
      unsubscribe?.();
    };
  }, [firestore, sessionId]);

  // ── Drive transitions when status changes ──────────────────────────────
  useEffect(() => {
    if (!session) return;
    const status = session.status;

    if (status === "rejected") {
      setPhase("rejected");
      return;
    }
    if (status === "expired") {
      setPhase("expired");
      return;
    }
    if (status === "completed") {
      setPhase("completed");
      return;
    }
    if (status === "failed") {
      setPhase("failed");
      if (!error) {
        setError({
          category: (session.failure_reason as ExportFailureCategory | null) ?? "decryption_error",
        });
      }
      return;
    }
    if (status === "pending") {
      // First time we see the doc: handshake to awaiting_approval.
      if (arrivalRecordedRef.current) return;
      arrivalRecordedRef.current = true;
      setPhase("awaiting_approval");
      void recordWebClientArrival(firestore, sessionId, userAgent).catch(() => {
        setFailed("firestore_permission_error");
      });
      return;
    }
    if (status === "awaiting_approval") {
      setPhase("awaiting_approval");
      return;
    }
    if (status === "approved") {
      // KEK is still encrypted on Firestore; unwrap once then move to consumed.
      if (kekRef.current) {
        setPhase("approved");
        return;
      }
      void (async () => {
        try {
          if (!session.encrypted_kek || !session.iv) {
            throw new Error("approved session is missing encrypted_kek/iv");
          }
          const transportKey = decodeBase64Url(transportKeyB64Url);
          const kekBytes = await decryptEncryptedKek({
            transportKey,
            encryptedKekBase64: session.encrypted_kek,
            ivBase64: session.iv,
          });
          zeroFill(transportKey);
          const cryptoKey = await importKekNonExtractable(kekBytes);
          zeroFill(kekBytes);
          kekRef.current = cryptoKey;
          setPhase("consuming");
          await markSessionConsumed(firestore, sessionId);
        } catch {
          setFailed("decryption_error");
        }
      })();
      return;
    }
    if (status === "consumed") {
      setPhase("consumed" as ExportPhase);
      // Consumer (UI) will call startExport() after the user picks a save dir.
      return;
    }
  }, [session, firestore, sessionId, transportKeyB64Url, userAgent, setFailed, error]);

  // ── User-triggered: open save dialog and run the export ─────────────────
  const startExport = useCallback(async () => {
    if (!session || !kekRef.current) {
      setFailed("session_invalid");
      return;
    }
    setPhase("exporting");
    setProgress(INITIAL_PROGRESS);
    try {
      const handle = await openSaveFile(defaultExportFilename(session.family_id));
      const entries = collectEntries(
        runExport({
          firestore,
          storage,
          familyId: session.family_id,
          kek: kekRef.current,
          onProgress: setProgress,
        }),
      );
      const totalBytes = await streamZipToFile(handle, entries);
      // Reach completed after the stream closes.
      await markSessionCompleted(firestore, sessionId);
      setPhase("completed");
      setProgress((p) => (p ? { ...p, phase: "finalizing", mediaBytes: totalBytes } : p));
    } catch (e) {
      if (e instanceof BrowserUnsupportedError) {
        setFailed("browser_unsupported");
        return;
      }
      setFailed("disk_write_error", String(e));
    }
  }, [session, firestore, storage, sessionId, setFailed]);

  return useMemo(
    () => ({ phase, session, progress, error, startExport }),
    [phase, session, progress, error, startExport],
  );
}

function defaultExportFilename(familyId: string): string {
  const stamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "")
    .replace("T", "-")
    .replace("Z", "");
  return `berry_buds_export_${familyId}_${stamp}.zip`;
}

/** Adapt the AsyncGenerator to AsyncIterable for `streamZipToFile`. */
async function* collectEntries(
  source: AsyncGenerator<ZipEntry, void, void>,
): AsyncGenerator<ZipEntry, void, void> {
  for await (const e of source) yield e;
}
